import { generateId } from './geometry';

// OBJ-Datei parsen und Punkte/Wände extrahieren
export async function importOBJ(file, scale = 1000) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const result = parseOBJ(text, scale);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
    reader.readAsText(file);
  });
}

// OBJ-Text parsen
function parseOBJ(text, scale) {
  const lines = text.split('\n');
  const vertices = [];
  const edges = new Set();

  // Vertices und Faces parsen
  for (const line of lines) {
    const parts = line.trim().split(/\s+/);
    const type = parts[0];

    if (type === 'v') {
      // Vertex: v x y z
      const x = parseFloat(parts[1]) * scale;
      const y = parseFloat(parts[2]) * scale;
      const z = parseFloat(parts[3]) * scale;
      vertices.push({ x, y, z });
    } else if (type === 'f') {
      // Face: f v1 v2 v3 ... (1-indexed)
      const indices = parts.slice(1).map(p => {
        // Format kann sein: v, v/vt, v/vt/vn, v//vn
        const idx = parseInt(p.split('/')[0], 10) - 1;
        return idx;
      });

      // Kanten aus Face extrahieren
      for (let i = 0; i < indices.length; i++) {
        const a = indices[i];
        const b = indices[(i + 1) % indices.length];
        // Kante normalisieren (kleinerer Index zuerst)
        const edgeKey = a < b ? `${a}-${b}` : `${b}-${a}`;
        edges.add(edgeKey);
      }
    } else if (type === 'l') {
      // Line: l v1 v2
      const a = parseInt(parts[1], 10) - 1;
      const b = parseInt(parts[2], 10) - 1;
      const edgeKey = a < b ? `${a}-${b}` : `${b}-${a}`;
      edges.add(edgeKey);
    }
  }

  // Punkte mit Deduplizierung erstellen
  const pointMap = new Map(); // "x,y" -> pointId
  const points = [];

  vertices.forEach((v, idx) => {
    // Nur X und Y für 2D-Grundriss (Z ignorieren oder als Höhe nutzen)
    const key = `${Math.round(v.x)},${Math.round(v.y)}`;

    if (!pointMap.has(key)) {
      const point = {
        id: generateId(),
        x: Math.round(v.x),
        y: Math.round(v.y),
        z: 2750, // Standard-Höhe
        originalIndex: idx,
      };
      points.push(point);
      pointMap.set(key, point.id);
    }
  });

  // Kanten zu Wänden konvertieren
  const walls = [];
  const processedEdges = new Set();

  for (const edgeKey of edges) {
    const [aIdx, bIdx] = edgeKey.split('-').map(Number);

    const aVertex = vertices[aIdx];
    const bVertex = vertices[bIdx];

    if (!aVertex || !bVertex) continue;

    const aKey = `${Math.round(aVertex.x)},${Math.round(aVertex.y)}`;
    const bKey = `${Math.round(bVertex.x)},${Math.round(bVertex.y)}`;

    const startPointId = pointMap.get(aKey);
    const endPointId = pointMap.get(bKey);

    if (!startPointId || !endPointId || startPointId === endPointId) continue;

    // Duplikat-Check
    const wallKey = startPointId < endPointId
      ? `${startPointId}-${endPointId}`
      : `${endPointId}-${startPointId}`;

    if (processedEdges.has(wallKey)) continue;
    processedEdges.add(wallKey);

    walls.push({
      id: generateId(),
      startPointId,
      endPointId,
      spaceIds: [],
      typeRef: null,
      openings: [],
    });
  }

  // originalIndex entfernen
  points.forEach(p => delete p.originalIndex);

  return { points, walls };
}

// Bounding Box berechnen
export function calculateBoundingBox(points) {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }

  let minX = Infinity, minY = Infinity;
  let maxX = -Infinity, maxY = -Infinity;

  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }

  return { minX, minY, maxX, maxY };
}

// Import zentrieren
export function centerImport(points, walls, targetCenter = { x: 0, y: 0 }) {
  const bbox = calculateBoundingBox(points);
  const centerX = (bbox.minX + bbox.maxX) / 2;
  const centerY = (bbox.minY + bbox.maxY) / 2;

  const offsetX = targetCenter.x - centerX;
  const offsetY = targetCenter.y - centerY;

  const centeredPoints = points.map(p => ({
    ...p,
    x: p.x + offsetX,
    y: p.y + offsetY,
  }));

  return { points: centeredPoints, walls };
}
